import {
  border,
  BorderProps,
  color,
  ColorProps,
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  position,
  PositionProps,
  ResponsiveValue,
  space,
  SpaceProps,
  typography,
  TypographyProps,
} from "styled-system";
import React, { InputHTMLAttributes } from "react";
import * as CSS from "csstype";
import { Flex } from "../Flex";
import { Label } from "../Label";
import { Span } from "../Span";
import styled from "styled-components";
import { InputTextProps } from "../InputText/InputText";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Icon } from "../Icon";

/* eslint-disable  @typescript-eslint/no-explicit-any */
export interface DatePickerProps {
  onChangeDate: (date: Date | null) => void;
  isDayEnabled: (date: Date) => boolean;
  selectedDate?: Date | undefined;
  displayValue?: string | undefined;
  minDate?: Date;
  maxDate?: Date;
  openOnDate?: Date;
  label?: string;
  displayLabel?: ResponsiveValue<CSS.Property.Display, any>;
  error?: boolean;
  optional?: boolean;
}

const FlexWrapper = styled(Flex)`
  box-sizing: border-box;
`;

const LabelWrapper = styled(Label)<
  Omit<
    DatePickerProps,
    "onChangeDate" | "displayValue" | "isDayEnabled" | "maxDate" | "minDate"
  > &
    ColorProps &
    InputHTMLAttributes<HTMLInputElement>
>`
  color: ${({ disabled, theme }) => disabled && theme.colors.disabledColor};
`;

const OptionalSpanWrapper = styled(Span)<
  Omit<
    DatePickerProps,
    "onChangeDate" | "displayValue" | "isDayEnabled" | "maxDate" | "minDate"
  > &
    ColorProps &
    InputHTMLAttributes<HTMLInputElement>
>`
  color: ${({ theme }) => theme.colors.neutrals.mn60};
`;

const DatePickerWrapper = styled.div<
  InputTextProps &
    BorderProps &
    ColorProps &
    SpaceProps &
    TypographyProps &
    InputHTMLAttributes<HTMLInputElement>
>`
  .react-datepicker {
    font-family: ${({ theme }) => theme.fonts.current};
  }

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__day--selected {
    background-color: ${({ theme }) => theme.colors.hydroDark};
  }

  .react-datepicker__input-container input {
    ${border}
    ${layout}
        ${color}
        ${space}
        ${typography}

        width: 100%;
    outline: none;
    border-color: ${({ error, theme }) =>
      (error && theme.colors.danger) || theme.colors.disabledBorder};
    box-sizing: border-box;

    &:focus {
      border-color: ${({ theme }) => theme.colors.hydro};
      box-shadow: ${({ theme }) => theme.shadows.inputFocus};
    }

    &:disabled {
      color: ${({ theme }) => theme.colors.disabledColor};
      background-color: ${({ theme }) => theme.colors.disabledBackground};
      border-color: ${({ theme }) => theme.colors.disabledBorder};
    }

    &::placeholder {
      color: ${({ disabled, theme }) =>
        disabled ? theme.colors.disabledColor : theme.colors.nightSky};
    }
  }
`;

const DatePickerInput = styled.input<
  InputTextProps &
    BorderProps &
    ColorProps &
    SpaceProps &
    TypographyProps &
    InputHTMLAttributes<HTMLInputElement>
>`
  ${border}
  ${layout}
    ${color}
    ${space}
    ${typography}
`;

export const IconWrapper = styled.span<
  ColorProps &
    FlexboxProps &
    SpaceProps &
    TypographyProps &
    LayoutProps &
    BorderProps &
    PositionProps & { disabled?: boolean }
>`
  ${color}
  ${space}
    ${typography}
    ${layout}
    ${border}
    ${flexbox}
    ${position}

    position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: ${({ disabled }) => (disabled ? "none" : "pointer")};
`;

const InputWithIcon = React.forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ value, onClick, disabled, placeholder }, ref) => (
  <>
    <DatePickerInput
      id={"date-picker-input"}
      role={"textbox"}
      value={value}
      onClick={onClick}
      disabled={disabled}
      placeholder={placeholder}
      ref={ref}
      readOnly
    />
    <IconWrapper id={"date-picker-input-icon"} onClick={onClick} disabled={disabled}>
      <Icon icon={"today"} color={"nightSky"}></Icon>️
    </IconWrapper>
  </>
));

export const DatePicker: React.FC<
  DatePickerProps &
    BorderProps &
    ColorProps &
    FlexboxProps &
    SpaceProps &
    TypographyProps &
    LayoutProps &
    InputHTMLAttributes<HTMLInputElement>
> = ({
  id,
  onChangeDate,
  isDayEnabled,
  selectedDate,
  minDate,
  maxDate,
  openOnDate,
  displayValue,
  label,
  displayLabel,
  placeholder,
  error,
  disabled,
  optional,
  width,
  maxWidth,
  mb,
  mt,
  color,
  minWidth,
  flexBasis,
  flexGrow,
  ...props
}) => {
  return (
    <FlexWrapper
      flexDirection="column"
      flexGrow={flexGrow}
      flexBasis={flexBasis}
      minWidth={minWidth}
      maxWidth={maxWidth}
      width={width}
      mt={mt}
      mb={mb}
    >
      <Flex pl={2} pr={2} mb={1} justifyContent="space-between">
        {label && (
          <>
            <LabelWrapper
              htmlFor={id}
              fontSize="body"
              lineHeight="body"
              disabled={disabled}
              color={color}
              display={displayLabel}
              data-testid="input-text-label"
            >
              {label}
            </LabelWrapper>
          </>
        )}

        {optional && (
          <OptionalSpanWrapper
            ml="auto"
            mr={2}
            fontSize={1}
            lineHeight={1}
            data-testid="input-text-optional"
          >
            Optional
          </OptionalSpanWrapper>
        )}
      </Flex>

      <DatePickerWrapper
        id={id}
        error={error}
        disabled={disabled}
        border="2px solid"
        borderRadius="input"
        borderColor="disabledBackground"
        fontFamily="inherit"
        pl={3}
        py={3}
        fontSize={2}
        lineHeight={2}
        width={width}
        {...props}
        type="text"
      >
        <ReactDatePicker
          id={"date-picker-input"}
          placeholderText={placeholder}
          onChange={(date: Date | null) => onChangeDate(date)}
          filterDate={isDayEnabled}
          popperPlacement="bottom"
          value={displayValue}
          selected={selectedDate}
          openToDate={openOnDate}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          customInput={<InputWithIcon />}
        ></ReactDatePicker>
      </DatePickerWrapper>
    </FlexWrapper>
  );
};

DatePicker.defaultProps = {
  error: false,
  disabled: false,
  optional: false,
  displayLabel: "inline-block",
};
